import { useCallback, useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import DefaultLayout from '../../layout/DefaultLayout';
import BreadcrumbTwo from '../../components/Breadcrumbs/BreadcrumbTwo';
import LoadingOverlay from '../../components/Loading/LoadingOverlay';
import TokenConfigurationForm from '../../components/auth/tokenConfiguration/TokenConfigurationForm';
import {
  fromMinutes,
  toMinutes,
  validateDuration,
} from '../../components/auth/tokenConfiguration/tokenDuration';
import TokenConfigurationService from '../../services/auth/TokenConfigurationService';
import useAlerts from '../../hooks/useAlerts';
import useConfirm from '../../hooks/useConfirmModal';
import {
  DEFAULT_TOKEN_DURATION_MINUTES,
  type CustomResponse,
  type TokenConfiguration,
  type TokenDurationUnit,
} from '../../types/auth/TokenConfigurationTypes';

const TokenConfigurationPage = () => {
  const [configuration, setConfiguration] = useState<TokenConfiguration | null>(
    null
  );
  const [value, setValue] = useState<number | ''>(8);
  const [unit, setUnit] = useState<TokenDurationUnit>('hours');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { show: showAlert } = useAlerts();
  const confirm = useConfirm();

  const showRequestError = useCallback(
    (requestError: unknown, fallback: string) => {
      const apiError = requestError as AxiosError<CustomResponse<unknown>>;
      showAlert(
        'error',
        apiError.response?.data?.title || 'Error',
        apiError.response?.data?.message || fallback
      );
    },
    [showAlert]
  );

  const loadConfiguration = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await TokenConfigurationService.get();
      setConfiguration(response.data);
      setUnit('hours');
      setValue(response.data.durationHours);
      setError(null);
    } catch (requestError) {
      showRequestError(
        requestError,
        'No se pudo consultar la configuración del token.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [showRequestError]);

  useEffect(() => {
    void loadConfiguration();
  }, [loadConfiguration]);

  const handleUnitChange = (nextUnit: TokenDurationUnit) => {
    if (value !== '') {
      setValue(fromMinutes(toMinutes(value, unit), nextUnit));
    }
    setUnit(nextUnit);
    setError(null);
  };

  const handleSave = async () => {
    const validationError = validateDuration(
      value === '' ? Number.NaN : value,
      unit
    );
    setError(validationError);
    if (validationError || value === '') return;

    try {
      setIsSubmitting(true);
      const response = await TokenConfigurationService.save(
        { durationMinutes: toMinutes(value, unit) },
        configuration !== null
      );
      setConfiguration(response.data);
      setValue(fromMinutes(response.data.durationMinutes, unit));
      showAlert(response.type, response.title, response.message);
    } catch (requestError) {
      showRequestError(
        requestError,
        'No se pudo guardar la configuración del token.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetConfiguration = async () => {
    try {
      setIsSubmitting(true);
      const response = await TokenConfigurationService.remove();
      setConfiguration(null);
      setUnit('hours');
      setValue(DEFAULT_TOKEN_DURATION_MINUTES / 60);
      setError(null);
      showAlert(response.type, response.title, response.message);
    } catch (requestError) {
      showRequestError(
        requestError,
        'No se pudo restablecer la configuración del token.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    confirm({
      title: 'Restablecer duración del token',
      message:
        'Se eliminará la configuración actual y los nuevos tokens volverán a expirar después de 8 horas. ¿Deseas continuar?',
      icon: 'warning',
      confirmText: 'Sí, restablecer',
      cancelText: 'Cancelar',
      onConfirm: () => void resetConfiguration(),
    });
  };

  return (
    <DefaultLayout>
      <BreadcrumbTwo />
      {isLoading ? (
        <LoadingOverlay />
      ) : (
        <TokenConfigurationForm
          value={value}
          unit={unit}
          updatedAt={configuration?.updatedAt ?? null}
          error={error}
          loading={isSubmitting}
          onValueChange={(nextValue) => {
            setValue(nextValue);
            setError(null);
          }}
          onUnitChange={handleUnitChange}
          onSubmit={() => void handleSave()}
          onReset={handleReset}
        />
      )}
    </DefaultLayout>
  );
};

export default TokenConfigurationPage;
